/**
 * DEV-2521: explicit booking kinds for the owner booking form.
 *
 * These unions are intentionally separate from the broad `ReservationType` display enum.
 * They describe what the owner is creating; the API `type` string is derived explicitly
 * through {@link bookingKindToApiType}.
 */

export type BookingKind = 'owner' | 'guest' | 'maintenance';

export type MaintenanceType = 'owner-access' | 'vendor' | 'maintenance' | 'housekeeping';

export const BOOKING_KINDS: readonly BookingKind[] = ['owner', 'guest', 'maintenance'];

export const MAINTENANCE_TYPES: readonly MaintenanceType[] = [
  'owner-access',
  'vendor',
  'maintenance',
  'housekeeping'
];

/**
 * Block types owners can create, each surfaced as its own Type step choice. Vendor and
 * Housekeeping blocks are created as Maintenance, with the vendor name folded into notes.
 */
export const CREATABLE_MAINTENANCE_TYPES: readonly MaintenanceType[] = ['owner-access', 'maintenance'];

export const DEFAULT_BOOKING_KIND: BookingKind = 'owner';
export const DEFAULT_MAINTENANCE_TYPE: MaintenanceType = 'maintenance';

/** Owner-facing Type step choices. Owner Access is a maintenance block with that subtype. */
export type BookingTypeChoice = BookingKind | 'owner-access';

export const BOOKING_TYPE_CHOICES: readonly BookingTypeChoice[] = [
  'owner',
  'guest',
  'maintenance',
  'owner-access'
];

export function bookingTypeChoiceFor(
  kind: BookingKind,
  maintenanceType: MaintenanceType | null
): BookingTypeChoice {
  if (kind === 'maintenance' && maintenanceType === 'owner-access') {
    return 'owner-access';
  }
  return kind;
}

export function selectionForBookingTypeChoice(choice: BookingTypeChoice): {
  kind: BookingKind;
  maintenanceType: MaintenanceType | null;
} {
  switch (choice) {
    case 'owner-access':
      return { kind: 'maintenance', maintenanceType: 'owner-access' };
    case 'maintenance':
      return { kind: 'maintenance', maintenanceType: 'maintenance' };
    default:
      return { kind: choice, maintenanceType: null };
  }
}

/** API `type` strings accepted by `POST /api/owner/reservation/booking`. */
export type BookingApiType =
  | 'Owner'
  | 'Guest of Owner'
  | 'Owner Access'
  | 'Vendor'
  | 'Maintenance'
  | 'Housekeeping';

const STAY_API_TYPE: Record<Exclude<BookingKind, 'maintenance'>, BookingApiType> = {
  owner: 'Owner',
  guest: 'Guest of Owner'
};

const MAINTENANCE_API_TYPE: Record<MaintenanceType, BookingApiType> = {
  'owner-access': 'Owner Access',
  vendor: 'Vendor',
  maintenance: 'Maintenance',
  housekeeping: 'Housekeeping'
};

/** The four maintenance API types owners may create (Internal/External remain staff-only). */
export const MAINTENANCE_BLOCK_API_TYPES: readonly string[] = MAINTENANCE_TYPES.map(
  (subtype) => MAINTENANCE_API_TYPE[subtype]
);

export function isBookingKind(value: unknown): value is BookingKind {
  return typeof value === 'string' && (BOOKING_KINDS as readonly string[]).includes(value);
}

export function isMaintenanceType(value: unknown): value is MaintenanceType {
  return typeof value === 'string' && (MAINTENANCE_TYPES as readonly string[]).includes(value);
}

export function isCreatableMaintenanceType(value: unknown): value is MaintenanceType {
  return typeof value === 'string' && (CREATABLE_MAINTENANCE_TYPES as readonly string[]).includes(value);
}

export function isStayKind(kind: BookingKind): kind is 'owner' | 'guest' {
  return kind === 'owner' || kind === 'guest';
}

/**
 * Explicit kind/subtype to API `type` mapping. Throws when a maintenance booking has no
 * subtype so an incomplete form can never reach the server with a guessed type.
 */
export function bookingKindToApiType(
  kind: BookingKind,
  maintenanceType?: MaintenanceType | null
): BookingApiType {
  if (kind === 'maintenance') {
    if (!maintenanceType || !isMaintenanceType(maintenanceType)) {
      throw new Error('A maintenance block requires a maintenance type.');
    }
    return MAINTENANCE_API_TYPE[maintenanceType];
  }
  return STAY_API_TYPE[kind];
}

/** Reverse lookup from a persisted API/Salesforce `type` to the owner-facing subtype. */
export function maintenanceTypeFromApiType(apiType: string | null | undefined): MaintenanceType | null {
  if (!apiType) return null;
  const match = MAINTENANCE_TYPES.find((subtype) => MAINTENANCE_API_TYPE[subtype] === apiType);
  return match ?? null;
}

/** True for the four owner-creatable maintenance block types. Never inspects guest flags. */
export function isMaintenanceBlockType(apiType: string | null | undefined): boolean {
  return !!apiType && MAINTENANCE_BLOCK_API_TYPES.includes(apiType);
}

export const BOOKING_KIND_LABELS: Record<BookingKind, string> = {
  owner: 'Owner Booking',
  guest: 'Guest Booking',
  maintenance: 'Maintenance'
};

export const BOOKING_KIND_DESCRIPTIONS: Record<BookingKind, string> = {
  owner: 'Stay at your property yourself.',
  guest: 'Reserve a stay for a guest you invite.',
  maintenance: 'Schedule a vendor maintenance block.'
};

export const MAINTENANCE_TYPE_LABELS: Record<MaintenanceType, string> = {
  'owner-access': 'Owner Access',
  vendor: 'Vendor',
  maintenance: 'Maintenance',
  housekeeping: 'Housekeeping'
};

export const MAINTENANCE_TYPE_DESCRIPTIONS: Record<MaintenanceType, string> = {
  'owner-access': 'Schedule a visit to your property.',
  vendor: 'Schedule access for a vendor.',
  maintenance: 'Reserve time for maintenance work.',
  housekeeping: 'Reserve time for cleaning you arrange.'
};

export const BOOKING_TYPE_CHOICE_LABELS: Record<BookingTypeChoice, string> = {
  ...BOOKING_KIND_LABELS,
  'owner-access': MAINTENANCE_TYPE_LABELS['owner-access']
};

export const BOOKING_TYPE_CHOICE_DESCRIPTIONS: Record<BookingTypeChoice, string> = {
  ...BOOKING_KIND_DESCRIPTIONS,
  'owner-access': MAINTENANCE_TYPE_DESCRIPTIONS['owner-access']
};

export const MAINTENANCE_FORM_COPY =
  'Reserve time for a visit or work you arrange. This does not request a vendor or cleaning service. For emergency access or immediate assistance, contact us directly.';

/** Display label for a persisted maintenance `type`, falling back to the raw value. */
export function maintenanceBlockLabel(apiType: string | null | undefined): string {
  const subtype = maintenanceTypeFromApiType(apiType);
  return subtype ? MAINTENANCE_TYPE_LABELS[subtype] : apiType ?? '';
}
